// Copyright (c) StrangeBee 2024

import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import useListQuery from '@Hooks/useListQuery';
import THSelector from '@Molecules/THSelector/THSelector';
import { ADMIN_ORG_ID } from '@Types';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { OrganisationOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';

import './OrgSelector.scss';

interface OrgSelectorProps {
    value?: string[];
    onChange?: (value: string[]) => void;
}

const remove =
    (item: any) =>
    (e: any): boolean =>
        e !== item;

export const OrgSelector = ({ value = [], onChange }: OrgSelectorProps): ReactElement => {
    const local = useLocalMessage();

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listOrganisation' }],
                name: 'get-all-organisations',
            },
            sort: ['+name'],
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const onClickHandler = useCallback(
        (item) => {
            const newValues = value?.includes(item) ? value?.filter(remove(item)) : [...value, item];
            onChange?.(newValues);
        },
        [onChange, value],
    );

    const orgListTreated = useMemo(() => {
        return orgList
            .reduce<string[]>((acc, { name }) => (name !== ADMIN_ORG_ID ? [...acc, name] : acc), [])
            .sort((a, b) => (a.toLocaleLowerCase().charCodeAt(0) - b.toLocaleLowerCase().charCodeAt(0)) as number);
    }, [orgList]);

    return (
        <THSelector<string>
            dataSource={orgListTreated} //
            className="org-selector"
            value={value}
            customSelect={(item, value) => value.includes(item)}
            onChange={undefined}
            onClick={onClickHandler}
            filtering={{
                search: (item) => item,
                placeholder: local('general.search'),
            }}
            displayEmpty={<DisplayEmptyData label={local('organisation.none')} />}
        >
            {(name, isSelected) => (
                <div className="global-user-org-selector__name">
                    {isSelected ? <ICONS.CheckCircleTwoTone /> : <ICONS.MinusCircleOutlined />}
                    {name}
                </div>
            )}
        </THSelector>
    );
};
